/* eslint-disable react-hooks/exhaustive-deps */
import PropTypes from 'prop-types';
import { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Chip, Fade, Popper, Paper, ClickAwayListener, Tooltip } from '@mui/material';
import { Iconify } from 'src/components';
import { getDrafts, deleteDraft, formatDraftTime } from 'src/utils/callScriptDraftDB';
import { localStorageGetItem, localStorageSetItem } from 'src/utils/storage-available';

const GUIDE_KEY = 'callScriptDraftGuideShown';

/**
 * DraftRecoveryChip — small chip next to the save indicator in the call script header.
 * Click opens a popover to browse and restore saved drafts.
 */
const DraftRecoveryBanner = ({ practiceId, scriptCategory, currentDraftId, onRestore, previewMode }) => {
    const [drafts, setDrafts] = useState([]);
    const [visible, setVisible] = useState(false);
    const [showPopover, setShowPopover] = useState(false);
    const [showGuide, setShowGuide] = useState(false);
    const anchorRef = useRef(null);

    useEffect(() => {
        if (previewMode || !practiceId) return;

        const loadDrafts = async () => {
            const found = await getDrafts(practiceId, scriptCategory);
            const available = found.filter((d) => d.draftId !== currentDraftId);
            if (available.length > 0) {
                setDrafts(available);
                setVisible(true);

                const guideShown = localStorageGetItem(GUIDE_KEY);
                if (!guideShown) {
                    setTimeout(() => setShowGuide(true), 800);
                }
            }
        };
        loadDrafts();
    }, [practiceId, scriptCategory, previewMode]);

    const handleRestore = useCallback((draft) => {
        if (draft && onRestore) {
            onRestore(draft);
            setShowPopover(false);
            setVisible(false);
            setShowGuide(false);
        }
    }, [onRestore]);

    const handleDeleteDraft = useCallback(async (e, draftId) => {
        e.stopPropagation();
        await deleteDraft(draftId);
        const updated = drafts.filter((d) => d.draftId !== draftId);
        setDrafts(updated);
        if (updated.length === 0) {
            setVisible(false);
            setShowPopover(false);
        }
    }, [drafts]);

    const handleDismissGuide = useCallback(() => {
        setShowGuide(false);
        localStorageSetItem(GUIDE_KEY, 'true');
    }, []);

    const handleChipClick = useCallback(() => {
        if (drafts.length === 1) {
            // Single draft — restore immediately
            handleRestore(drafts[0]);
        } else {
            // Multiple drafts — open picker
            setShowPopover((prev) => !prev);
        }
        if (showGuide) {
            setShowGuide(false);
            localStorageSetItem(GUIDE_KEY, 'true');
        }
    }, [drafts, showGuide, handleRestore]);

    if (!visible || previewMode) return null;

    return (
        <>
            <Chip
                ref={anchorRef}
                icon={<Iconify icon="mdi:restore" width={14} />}
                label={drafts.length === 1 ? 'Restore draft' : `${drafts.length} drafts`}
                size="small"
                variant="outlined"
                onClick={handleChipClick}
                sx={{
                    height: 26,
                    fontSize: '11px',
                    fontWeight: 600,
                    borderColor: '#FFAB00',
                    bgcolor: '#FFF8E5',
                    color: '#B76E00',
                    cursor: 'pointer',
                    '& .MuiChip-icon': { color: '#B76E00', ml: 0.5 },
                    '&:hover': { borderColor: '#B76E00', bgcolor: '#FFF0CC' },
                }}
            />

            {/* Multi-draft picker popover */}
            <Popper
                open={showPopover}
                anchorEl={anchorRef.current}
                placement="bottom-end"
                sx={{ zIndex: 1400 }}
                transition
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={200}>
                        <Paper
                            sx={{
                                mt: 0.5,
                                borderRadius: '10px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                border: '1px solid',
                                borderColor: 'divider',
                                overflow: 'hidden',
                                minWidth: 260,
                                maxWidth: 340,
                            }}
                        >
                            <ClickAwayListener onClickAway={() => setShowPopover(false)}>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        sx={{ px: 1.5, pt: 1.25, pb: 0.5, display: 'block', fontWeight: 700, color: 'text.disabled', fontSize: '10px', letterSpacing: 0.5 }}
                                    >
                                        RECENT DRAFTS
                                    </Typography>

                                    {drafts.map((draft) => (
                                        <Box
                                            key={draft.draftId}
                                            onClick={() => handleRestore(draft)}
                                            sx={{
                                                px: 1.5,
                                                py: 0.75,
                                                cursor: 'pointer',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                '&:hover': { bgcolor: 'action.hover' },
                                                '&:last-of-type': { mb: 0.5 },
                                            }}
                                        >
                                            <Iconify icon="mdi:file-document-outline" width={16} sx={{ color: 'text.disabled', flexShrink: 0 }} />
                                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{ fontSize: '12px', fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                                                >
                                                    {draft.callerName || 'Unknown caller'}
                                                </Typography>
                                                <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                                                    {formatDraftTime(draft.updatedAt)}
                                                    {draft.scriptName ? ` · ${draft.scriptName}` : ''}
                                                </Typography>
                                            </Box>

                                            <Tooltip title="Delete" arrow placement="top">
                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={(e) => handleDeleteDraft(e, draft.draftId)}
                                                    sx={{ minWidth: 0, p: 0.25, color: 'text.disabled', '&:hover': { color: '#FF5630' } }}
                                                >
                                                    <Iconify icon="eva:close-fill" width={14} />
                                                </Button>
                                            </Tooltip>
                                        </Box>
                                    ))}
                                </Box>
                            </ClickAwayListener>
                        </Paper>
                    </Fade>
                )}
            </Popper>

            {/* One-time tooltip */}
            <Popper
                open={showGuide}
                anchorEl={anchorRef.current}
                placement="bottom"
                sx={{ zIndex: 1500 }}
                transition
            >
                {({ TransitionProps }) => (
                    <Fade {...TransitionProps} timeout={300}>
                        <Paper
                            sx={{
                                mt: 1,
                                px: 2,
                                py: 1.5,
                                borderRadius: '10px',
                                boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                                border: '1px solid',
                                borderColor: 'divider',
                                maxWidth: 240,
                            }}
                        >
                            <Typography variant="body2" sx={{ fontSize: '12px', color: 'text.secondary', lineHeight: 1.5 }}>
                                Your progress is saved locally so you can pick up where you left off.
                            </Typography>
                            <Button
                                size="small"
                                variant="text"
                                onClick={handleDismissGuide}
                                sx={{ mt: 0.5, fontSize: '11px', fontWeight: 600, color: 'text.secondary', px: 0, minWidth: 0, '&:hover': { color: 'text.primary' } }}
                            >
                                Got it
                            </Button>
                        </Paper>
                    </Fade>
                )}
            </Popper>
        </>
    );
};

DraftRecoveryBanner.propTypes = {
    practiceId: PropTypes.number,
    scriptCategory: PropTypes.string,
    currentDraftId: PropTypes.string,
    onRestore: PropTypes.func,
    previewMode: PropTypes.bool,
};

export default DraftRecoveryBanner;
